import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { APP_NAME, brandLogoSrc } from '../config/branding';
import { LogoutIcon, SunIcon, MoonIcon } from './Icons';
import './Navigation.css';

const Navigation: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  
  // 인증 상태 변경 시 디버깅
  useEffect(() => {
    console.log('Navigation: isAuthenticated 변경됨', isAuthenticated, 'user:', user, 'pathname:', location.pathname);
  }, [isAuthenticated, user, location.pathname]);

  // 라우트 변경 시 메뉴 닫기
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // 메뉴 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const toggleMenu = useCallback(() => {
    setMenuOpen(prev => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleLogout = async () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      await logout();
      navigate('/');
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path ? 'active' : '';
  };

  return (
    <nav className="navigation">
      <div className="nav-brand">
        <h1 className="nav-brand-title">
          <Link to="/" className="nav-brand-link">
            <img src={brandLogoSrc()} alt="" className="nav-brand-logo" width={28} height={28} />
            {APP_NAME}
          </Link>
        </h1>
      </div>

      {/* 오버레이 (모바일) — fixed라 플로우 밖 */}
      <div 
        className={`nav-overlay ${menuOpen ? 'open' : ''}`} 
        onClick={closeMenu}
      />

      <div className="nav-end">
        <div className={`nav-menu ${menuOpen ? 'open' : ''}`}>
        <Link 
          to="/"
          className={`nav-item ${isActive('/')}`}
        >
          홈
        </Link>
        
        <Link 
          to="/investments"
          className={`nav-item ${isActive('/investments')}`}
        >
          투자일지
        </Link>
        <Link 
          to="/asset"
          className={`nav-item ${isActive('/asset')}`}
        >
          자산관리
        </Link>
        
        {isAuthenticated ? (
          <div className="nav-user">
            <Link 
              to="/mypage"
              className={`user-name clickable ${isActive('/mypage') ? 'active' : ''}`}
              title="마이페이지"
            >
              {user?.nickname || user?.username}
            </Link>
            <button className="nav-logout" onClick={handleLogout}>
              <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><LogoutIcon size={16} color="currentColor" /></span> 로그아웃
            </button>
          </div>
        ) : (
          <div className="nav-auth">
            <Link 
              to="/login"
              className="nav-login-btn"
            >
              로그인
            </Link>
            <Link 
              to="/register"
              className="nav-register-btn"
            >
              회원가입
            </Link>
          </div>
        )}
        </div>

        <button
          className="nav-theme-toggle"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
          title={theme === 'dark' ? '라이트 모드' : '다크 모드'}
        >
          {theme === 'dark' ? <SunIcon size={18} color="currentColor" /> : <MoonIcon size={18} color="currentColor" />}
        </button>

        <button 
          className={`nav-hamburger ${menuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="메뉴 열기/닫기"
          aria-expanded={menuOpen}
        >
          <div className="hamburger-icon">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
